import clsx from 'clsx'
import { useMemo, type FC } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'

import usePenetapanSummaryDinas from 'app/KinerjaDinasPage/hooks/usePenetapanSummaryDinas'
import { delimit } from 'utils/number'
import { predicateLabel } from 'app/KinerjaPSPage/utils/states'
import StateError from 'app/KinerjaShared/components/StateError'
import { useIsPrior2025 } from 'app/KinerjaShared/hooks/useIsPrior2025'

import type {
  TPredicateKey,
  TPredicates,
  TResponsePenilaianSummaryData,
} from 'types/kinerja-dinas'

interface CardSummaryPenetapanProps {
  enabled?: boolean
  hideOnError?: boolean
  onClickSend: () => void
}

const containerClassNames = 'rounded py-3 px-4 mt-6 mb-3 border'
const containerDisabledClassNames = 'bg-gray-5 border-subdued'
const containerSuccessClassNames = 'bg-blue-5 border-blue-70'

const BlockLoading = () => {
  return (
    <div
      data-testid="section-ps-penilaian-summary-loading"
      className={clsx(containerClassNames, containerDisabledClassNames)}
    >
      <div className="grid grid-cols-6 gap-4">
        <div className="col-span-3 flex flex-col justify-between items-start gap-4">
          <div className="space-y-1">
            <Shimmer width={400} height={22} />
            <Shimmer width={500} height={22} />
          </div>
          <Shimmer width={210} height={48} />
        </div>
        <div className="col-span-3">
          <div className={clsx('grid grid-cols-2 divide-x', 'divide-subdued')}>
            {Array.from({ length: 2 }).map((_, index) => {
              return (
                <div key={`chunk-${index}`} className="col-span-1 space-y-3">
                  <div className="flex items-center justify-between px-4">
                    <Shimmer width={170} height={22} />
                    <Shimmer width={45} height={22} />
                  </div>
                  <div className="flex items-center justify-between px-4">
                    <Shimmer width={150} height={22} />
                    <Shimmer width={65} height={22} />
                  </div>
                  <div className="flex items-center justify-between px-4">
                    <Shimmer width={130} height={22} />
                    <Shimmer width={85} height={22} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

const BlockError = ({
  refetch,
  hideOnError,
}: {
  refetch: () => void
  hideOnError?: boolean
}) => {
  if (hideOnError)
    return <div data-testid="section-ps-penilaian-summary-error" />

  return (
    <div
      data-testid="section-ps-penilaian-summary-error"
      className={clsx(containerClassNames, containerDisabledClassNames)}
    >
      <StateError onClick={() => refetch()} />
    </div>
  )
}

const BlockCompleted = () => {
  return (
    <div
      data-testid="section-ps-penilaian-summary-completed"
      className={clsx(
        containerClassNames,
        'bg-green-5 border border-green-50 rounded p-2',
        'flex gap-4 max-w-xl'
      )}
    >
      <Icon fontSize="lg" color="success" className="!text-4xl">
        check_circle_outlined
      </Icon>
      <div className="space-y-1">
        <Text variant="heading-sm" color="success" className="!text-green-60">
          Semua Rekomendasi Predikat Kinerja sudah dikirim!
        </Text>
        <Text variant="body-sm" color="subdued">
          Terima kasih sudah menyelesaikan tahap ini.
        </Text>
      </div>
    </div>
  )
}

const BlockData = ({
  summaryKs,
  summaryGuru,
  deliverable,
  predicateChunks,
  onClick,
}: {
  summaryKs: TResponsePenilaianSummaryData
  summaryGuru: TResponsePenilaianSummaryData
  deliverable: number
  predicateChunks: TPredicates[]
  onClick: () => void
}) => {
  const hasData = deliverable >= 1
  const isPriorTo2025 = useIsPrior2025()
  return (
    <div
      data-testid="section-ps-penilaian-summary-data"
      className={clsx(
        containerClassNames,
        hasData && containerSuccessClassNames,
        !hasData && containerDisabledClassNames
      )}
    >
      <div className="grid grid-cols-6 gap-4">
        <div className="col-span-3 flex flex-col justify-between items-start gap-4">
          <div className="space-y-1">
            <Text
              variant="heading-sm"
              className={clsx(
                hasData && '!text-blue-60',
                !hasData && '!text-disabled'
              )}
            >
              {deliverable
                ? isPriorTo2025
                  ? `${delimit(deliverable)} Predikat Kinerja dapat dikirim`
                  : `${delimit(
                      deliverable
                    )} Penetapan Predikat Kinerja dapat dikirim`
                : 'Belum ada Predikat Kinerja yang dapat dikirim'}
            </Text>
            <Text variant="body-sm" color="subdued">
              {hasData
                ? 'Jika belum selesai dinilai, Anda dapat kirim di pengiriman berikutnya.'
                : 'Selesaikan Penilaian Kinerja Pegawai terlebih dahulu.'}
            </Text>
          </div>
          <div className="flex items-center gap-4">
            <Button size="lg" disabled={!hasData} onClick={onClick}>
              <Icon
                fontSize="default"
                color={hasData ? 'inverse' : 'disabled'}
                className="mr-1"
              >
                mail_outline
              </Icon>
              Mulai kirim ({deliverable})
            </Button>

            <div>
              <Text variant="helper" color="subdued">
                Kepala Sekolah:{' '}
                {summaryKs?.belumDikirim >= 0 ? summaryKs?.belumDikirim : 0}{' '}
                orang
              </Text>
              <Text variant="helper" color="subdued">
                Guru:{' '}
                {summaryGuru?.belumDikirim >= 0 ? summaryGuru?.belumDikirim : 0}{' '}
                orang
              </Text>
            </div>
          </div>
        </div>

        <div className="col-span-3">
          <div
            className={clsx(
              'grid grid-cols-2 divide-x',
              hasData && 'divide-blue-40',
              !hasData && 'divide-subdued'
            )}
          >
            {predicateChunks?.map((chunk, index) => {
              return (
                <div key={`chunk-${index}`} className="col-span-1 space-y-3">
                  {Object.keys(chunk).map((key: TPredicateKey) => {
                    return (
                      <div
                        key={`chunk-${index}-${key}`}
                        className="flex items-center justify-between px-4"
                      >
                        <Text variant="action" color="subdued">
                          {predicateLabel(key, '-')}
                        </Text>
                        <Text variant="body-sm">
                          {delimit(chunk?.[key] || 0)}
                        </Text>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

const CardSummaryPenetapan: FC<CardSummaryPenetapanProps> = ({
  hideOnError = true,
  onClickSend,
}) => {
  const {
    isLoading: isLoadingSummary,
    isError: isErrorSummary,
    isSuccess: isSuccessSummary,
    isFetched: isFetchedSummary,
    summaryKs,
    summaryGuru,
    summary,
    refetch,
  } = usePenetapanSummaryDinas({})

  const predicateChunks = useMemo(() => {
    const predicates = { ...summary }
    delete predicates.belumDikirim

    const chunkSize = 3
    return Object.entries(predicates).reduce((acc, [key, value], index) => {
      const chunkIndex = Math.floor(index / chunkSize)
      if (!acc[chunkIndex]) {
        acc[chunkIndex] = {}
      }
      acc[chunkIndex][key] = value
      return acc
    }, []) as Array<TPredicates>
  }, [summary])

  const deliverable = summary?.belumDikirim || 0
  const isDeliverable = !(
    summaryKs?.belumDikirim === -1 && summaryGuru?.belumDikirim === -1
  )
  const isSuccess = !isLoadingSummary && isFetchedSummary && isSuccessSummary
  const isCompleted = isSuccess && !isDeliverable
  const hasData = isSuccess && isDeliverable

  return (
    <div data-testid="section-ps-penilaian-summary">
      {/* If the data is still loading, render the shimmer */}
      {isLoadingSummary && <BlockLoading />}
      {/* If there's an error, render the error state */}
      {isErrorSummary && (
        <BlockError refetch={refetch} hideOnError={hideOnError} />
      )}
      {/* If deliverable is -1, then render the Thank You message */}
      {isCompleted && <BlockCompleted />}
      {/* If the data is fetched and there's no error, render the summary */}
      {hasData && (
        <BlockData
          summaryKs={summaryKs}
          summaryGuru={summaryGuru}
          deliverable={deliverable}
          predicateChunks={predicateChunks}
          onClick={onClickSend}
        />
      )}
    </div>
  )
}

export default CardSummaryPenetapan
